import styled, { css } from 'styled-components';
import { useSettings } from '@wu/widget-core';
import { defaultSettings, isFacebook, isStandalone } from '../../../';
import type { ChatsChatMethod } from '../../../constants/types';
import { getInvertedColor } from '../../helpers';
import { Avatar } from '../components';
import { useStartTimeChat, useTyping, useWindowPosition } from '../utils';
import { BubbleDetail } from './BubbleDetail';
import { dotVariants } from './dots-types';

const MIN_MESSAGE_WIDTH = 200;

const getSpacing = (chatMethod?: ChatsChatMethod, timeOnHeader?: boolean) => {
  if (timeOnHeader) {
    return `10px 20px ${isStandalone ? 12 : 20}px 20px`;
  }

  return `20px 20px ${chatMethod === 'sendMessage' ? 32 : 12}px 20px`;
};

const getMessageWidth = (windowWidth: number, isAvatar: boolean) => {
  if (windowWidth < MIN_MESSAGE_WIDTH) {
    return '100%';
  }

  return isFacebook || !isStandalone || !isAvatar
    ? 'calc(100% - 66px)'
    : 'calc(100% - 36px)';
};

interface ChatLayoutProps {
  timeOnHeader?: boolean;
  isAvatar?: boolean;
  avatarSize?: number;
  messageShadow?: boolean;
  dotType?: 'whatsapp' | 'default';
}

export const ChatLayout = ({
  timeOnHeader = false,
  isAvatar = false,
  avatarSize,
  dotType = 'default',
  messageShadow = true
}: ChatLayoutProps) => {
  const {
    chatMethod,
    welcomeMessage,
    chatWallpaper = defaultSettings.chatWallpaper,
    messageBackgroundColor = defaultSettings.messageBackgroundColor
  } = useSettings();

  const { width: windowWidth } = useWindowPosition();
  const startTime = useStartTimeChat();
  const typing = useTyping();
  const borderRadius = isAvatar ? '16px' : '0 16px 16px 16px';
  const spacing = getSpacing(chatMethod, timeOnHeader);
  const width = getMessageWidth(windowWidth, isAvatar);
  const messageContentColor = getInvertedColor({
    color: messageBackgroundColor
  });
  const timeColor = getInvertedColor({ color: chatWallpaper });

  const Dots = dotVariants[dotType];

  const welcomeMessageWithTime = !timeOnHeader;

  return (
    <Component _spacing={spacing}>
      {timeOnHeader && (
        <Time>
          <TimeInner>
            <TimeValue _color={timeColor}>{startTime}</TimeValue>
          </TimeInner>
        </Time>
      )}
      <MessageContainer>
        {isAvatar && (
          <MessageUser>
            <Avatar size={avatarSize} />
          </MessageUser>
        )}

        <DotsContainer _show={typing}>
          <Dots
            background={messageBackgroundColor}
            color={messageContentColor}
          />
        </DotsContainer>

        <Message
          _borderRadius={borderRadius}
          _width={width}
          _shadow={messageShadow}
          _backgroundColor={messageBackgroundColor}
          _show={!typing}
          _isAvatar={isAvatar}
        >
          {!isAvatar && <BubbleDetail />}

          <MessageContent _color={messageContentColor}>
            <MessageGroup>
              <Text
                dangerouslySetInnerHTML={{
                  __html: welcomeMessage
                }}
                _small={welcomeMessageWithTime}
              />
              {welcomeMessageWithTime && <TimeBottom>{startTime}</TimeBottom>}
            </MessageGroup>
          </MessageContent>
        </Message>
      </MessageContainer>
    </Component>
  );
};

const Component = styled.div<{ _spacing: string }>`
  position: relative;
  padding: ${({ _spacing }) => _spacing};
  overflow: auto;
  max-height: 382px;
`;

const Time = styled.div`
  text-align: center;
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const TimeInner = styled.div`
  padding: 4px;
  border-radius: 5px;
  z-index: 1;
`;

const TimeValue = styled.div<{ _color?: string }>`
  font-size: 10px;
  position: relative;
  opacity: 0.3;
  color: ${({ _color }) => _color};
`;

const MessageContainer = styled.div`
  position: relative;
  display: flex;
  z-index: 1;
  transition: 0.3s ease opacity;
`;

const MessageUser = styled.div`
  min-height: 100%;
  margin-right: 10px;

  html[dir='rtl'] & {
    margin-right: 0;
    margin-left: 10px;
  }
`;

const Message = styled.div<{
  _width: string;
  _borderRadius: string;
  _show: boolean;
  _isAvatar: boolean;
  _shadow: boolean;
  _backgroundColor?: string;
}>`
  box-sizing: border-box;
  padding: 6px 14px;
  position: relative;
  transform-origin: top;
  z-index: 2;
  color: ${({ _backgroundColor }) => _backgroundColor};
  font-size: 15px;
  line-height: 1.39;
  max-width: ${({ _width }) => _width};
  border-radius: ${({ _borderRadius }) => _borderRadius};
  background-color: ${({ _backgroundColor }) => _backgroundColor};
  opacity: ${({ _show }) => (_show ? 1 : 0)};
  hyphens: auto;

  html[dir='rtl'] & {
    margin-right: 0;
    border-radius: ${({ _isAvatar }) =>
      _isAvatar ? '16px' : '16px 0 16px 16px'};
  }

  & p {
    margin: 0;
  }

  ${({ _shadow }) => _shadow && `box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.15);`}
`;

const MessageContent = styled.div<{ _color?: string }>`
  color: ${({ _color }) => _color};
`;

const Text = styled.div<{ _small?: boolean }>`
  ${({ _small }) =>
    _small
      ? css`
          font-size: 14px;
          line-height: 19px;
        `
      : css`
          font-size: 15px;
          line-height: 20px;
        `}
  overflow: hidden;
`;

const TimeBottom = styled.div`
  text-align: right;
  margin-left: 12px;
  font-size: 12px;
  line-height: 14px;
  opacity: 0.5;

  html[dir='rtl'] & {
    margin-left: 0;
    margin-right: 12px;
  }
`;

const MessageGroup = styled.div`
  display: flex;
  align-items: flex-end;
  color: inherit;

  &:first-child {
    flex: 1;
  }

  &:last-child {
    flex: 0;
  }
`;

const DotsContainer = styled.div<{ _show: boolean }>`
  opacity: ${({ _show }) => (_show ? 1 : 0)};
  pointer-events: ${({ _show }) => (_show ? 'all' : 'none')};
`;
